import { collection, deleteDoc, doc, getDoc, getDocs, onSnapshot, updateDoc } from 'firebase/firestore';
import { createDocument, isDocIdTaken } from '@/data/util_documentIds';
import { db } from '@/firebaseInit';
import { coll_guides, compareGuides, normaliseGuide, type GuideType } from '@/types/types_guide';

// Data-access layer for tour leaders / guides, backed by Firestore. Mirrors the
// shape of partnersRepo.ts so the autosave/history stack works the same way.

const guidesCollection = collection(db, coll_guides);

function toGuide(id: string, data: Record<string, unknown>): GuideType {
  return normaliseGuide(data as Partial<GuideType>, id);
}

export async function listGuides(): Promise<GuideType[]> {
  const snap = await getDocs(guidesCollection);
  return snap.docs.map((d) => toGuide(d.id, d.data())).sort(compareGuides);
}

export async function getGuide(id: string): Promise<GuideType | undefined> {
  const snap = await getDoc(doc(guidesCollection, id));
  return snap.exists() ? toGuide(snap.id, snap.data()) : undefined;
}

/**
 * Subscribes to the whole roster in real time. Mirrors Firestore's onSnapshot:
 * the initial value and every change after. Returns an unsubscribe function.
 *
 * Sorted here rather than with an `orderBy`, because `displayOrder` is only half
 * the ordering — the tie-break is a locale-aware name comparison, which
 * Firestore cannot do. The roster is a few dozen documents, so the client is the
 * right place for it.
 */
export function subscribeGuides(
  onChange: (guides: GuideType[]) => void,
  onError?: (error: Error) => void,
): () => void {
  return onSnapshot(
    guidesCollection,
    (snap) => onChange(snap.docs.map((d) => toGuide(d.id, d.data())).sort(compareGuides)),
    onError,
  );
}

/**
 * Whether a guide already exists under this id. For a form to show while
 * somebody types; `createGuide` is what keeps the id unique.
 */
export async function isGuideIdTaken(id: string): Promise<boolean> {
  return isDocIdTaken(guidesCollection, id);
}

/**
 * Creates a guide, refusing an id another guide already holds.
 *
 * Editing goes through `updateGuideFields` (the form autosaves per field), so
 * nothing here needs to double as an update.
 */
export async function createGuide(guide: GuideType): Promise<GuideType> {
  return createDocument(guidesCollection, guide);
}

/**
 * Field-level update of a single guide, used by autosave. `updateObj` may
 * contain dotted field paths (e.g. history.*). `updatedAt` is stamped here.
 */
export async function updateGuideFields(id: string, updateObj: Record<string, unknown>): Promise<void> {
  await updateDoc(doc(guidesCollection, id), {
    ...updateObj,
    updatedAt: new Date().toISOString(),
  });
}

/** Deletes a guide. */
export async function deleteGuide(id: string): Promise<void> {
  await deleteDoc(doc(guidesCollection, id));
}
